import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, RefreshCcw } from 'lucide-react'; // Icone Equipamento
import { Equipment, EquipmentType, EquipmentBrand, EquipmentFilter } from '../../types/equipment'; // Importa tipo
import { useTheme } from '@mui/material/styles';
import { ptBRGridLocale, getDataGridStyles } from '../../utils/gridLocalization';
import EquipmentService from '../../services/equipmentService';

// Mock data (Comentado pois agora usamos API)
/*
const mockEquipments: Equipment[] = [
  {
    id: 1,
    equipment_type_id: 1, // Usar IDs e campos corretos
    equipment_brand_id: 1,
    EquipmentType: { id: 1, name: 'Notebook', active: true }, // Exemplo de objeto aninhado
    EquipmentBrand: { id: 1, name: 'Dell', active: true },
    purchase_date: '2023-01-15',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 2,
    equipment_type_id: 5, 
    equipment_brand_id: 6,
    EquipmentType: { id: 5, name: 'Monitor', active: true },
    EquipmentBrand: { id: 6, name: 'Samsung', active: true },
    purchase_date: '2023-02-20',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 3,
    equipment_type_id: 2,
    equipment_brand_id: 5,
    EquipmentType: { id: 2, name: 'Mouse', active: true },
    EquipmentBrand: { id: 5, name: 'Logitech', active: true },
    purchase_date: '2022-11-01',
    discharge_date: '2024-05-10',
    discharge_reason: 'Defeito',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];
*/

// Objeto de tradução
const ptBRLocaleText = {
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página:',
  },
};

// Função simples para formatar data (definida localmente)
const formatDateSimple = (dateString: string | null | undefined, includeTime = false): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return date.toLocaleDateString('pt-BR', options);
  } catch (error) {
    return 'Data inválida';
  }
};

const EquipmentListPage = () => {
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState<Equipment | null>(null);

  // Estados para filtros
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [serialFilter, setSerialFilter] = useState<string>('');

  // Estados para opções dos filtros
  const [typeOptions, setTypeOptions] = useState<EquipmentType[]>([]);
  const [brandOptions, setBrandOptions] = useState<EquipmentBrand[]>([]);

  // Estados para paginação e ordenação
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'purchase_date', sort: 'desc' }]);

  const navigate = useNavigate();
  const theme = useTheme();
  const equipmentService = useMemo(() => new EquipmentService(), []);

  // Buscar tipos e marcas para filtros ao montar
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [types, brands] = await Promise.all([
          equipmentService.getEquipmentTypes(),
          equipmentService.getEquipmentBrands()
        ]);
        setTypeOptions(types);
        setBrandOptions(brands);
      } catch (err) {
        console.error("Erro ao carregar opções de filtro:", err);
        setError("Falha ao carregar opções de filtro.");
      }
    };
    fetchOptions();
  }, [equipmentService]);

  // Função para buscar equipamentos
  const fetchEquipments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: EquipmentFilter = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        // Passar IDs numéricos se não for 'all'
        equipment_type_id: typeFilter !== 'all' ? parseInt(typeFilter, 10) : undefined,
        equipment_brand_id: brandFilter !== 'all' ? parseInt(brandFilter, 10) : undefined,
        serial_number: serialFilter || undefined,
      };
      if (sortModel.length > 0) {
        filters.sortField = sortModel[0].field;
        filters.sortOrder = sortModel[0].sort ?? 'asc';
      }
      
      const response = await equipmentService.getAll(filters);
      setEquipments(response.data);
      setTotalRowCount(response.totalCount);
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar equipamentos.');
      setEquipments([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, typeFilter, brandFilter, serialFilter, equipmentService]);

  // Buscar equipamentos quando paginação, ordenação ou filtros mudam
  useEffect(() => {
    fetchEquipments();
  }, [fetchEquipments]); // useCallback garante que fetchEquipments só muda quando necessário

  // Handlers
  const handleAddNew = () => navigate('/equipment/new');
  const handleEdit = (id: number) => navigate(`/equipment/edit/${id}`);
  const handleDeleteClick = (equipment: Equipment) => {
    setEquipmentToDelete(equipment);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (!equipmentToDelete) return;
    console.log('Inativando equipamento (simulado) - IMPLEMENTAR API:', equipmentToDelete.id);
    // TODO: Implementar chamada à API para delete/inativação
    // Exemplo: await equipmentService.delete(equipmentToDelete.id);
    // Após sucesso, recarregar a lista:
    // fetchEquipments();
    // Por ora, apenas fecha o diálogo
    setDeleteDialogOpen(false);
    setEquipmentToDelete(null);
  };
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEquipmentToDelete(null);
  };
  
  const handlePaginationModelChange = (model: GridPaginationModel) => setPaginationModel(model);
  const handleSortModelChange = (model: GridSortModel) => setSortModel(model);

  // Handlers específicos para filtros
  const handleTypeFilterChange = (event: SelectChangeEvent<string>) => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setTypeFilter(event.target.value as string);
  };

  const handleBrandFilterChange = (event: SelectChangeEvent<string>) => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setBrandFilter(event.target.value as string);
  };

  const handleSerialFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Não resetar página ao digitar número de série para melhor UX
    // setPaginationModel(prev => ({ ...prev, page: 0 })); 
    setSerialFilter(event.target.value);
  };

  const handleClearFilters = () => {
    setTypeFilter('all');
    setBrandFilter('all');
    setSerialFilter('');
    setPaginationModel({ page: 0, pageSize: 10 }); // Resetar página ao limpar
    setSortModel([{ field: 'purchase_date', sort: 'desc' }]); // Resetar ordenação padrão
    // fetchEquipments(); // O useEffect já vai disparar a busca
  };

  const handleSearch = () => {
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Resetar página ao buscar
      fetchEquipments(); // Força a busca com os filtros atuais
  }

  const columns: GridColDef[] = [
    {
      field: 'EquipmentType',
      headerName: 'Tipo',
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (params) => params.row.EquipmentType?.name || '-'
    },
    {
      field: 'EquipmentBrand',
      headerName: 'Marca',
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (params) => params.row.EquipmentBrand?.name || '-'
    },
    {
      field: 'model',
      headerName: 'Modelo',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueGetter: (params) => params.value || '-'
    },
    {
      field: 'serial_number',
      headerName: 'Nº Série',
      flex: 1,
      minWidth: 150,
      sortable: true, 
      valueGetter: (params) => params.value || '-'
    },
    {
      field: 'purchase_date',
      headerName: 'Data Compra',
      flex: 1,
      minWidth: 120,
      sortable: true,
      valueGetter: (params) => formatDateSimple(params.value),
    },
    {
      field: 'discharge_date',
      headerName: 'Data Baixa',
      flex: 1,
      minWidth: 120,
      sortable: true,
      valueGetter: (params) => formatDateSimple(params.value),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box> 
          <Button 
            size="small" 
            color="primary"
            onClick={() => handleEdit(params.row.id)} 
            title="Editar"
            sx={{ minWidth: 'auto', mr: 0.5, p: '4px' }} 
          >
            <Edit size={18} />
          </Button>
           {/* <Button 
            size="small" 
            color="error" 
            onClick={() => handleDeleteClick(params.row as Equipment)}
            title="Excluir/Inativar"
            sx={{ minWidth: 'auto', p: '4px' }} 
           >
            <Trash2 size={18} />
           </Button> */}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Cabeçalho com título e botão Novo */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/* Alterar variant para h5 para diminuir o título */}
        <Typography variant="h5" component="h1">
          Equipamentos
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={handleAddNew}
          size="small"
          sx={{ fontSize: '0.875rem' }}
        >
          Novo Equipamento
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.875rem' }}>{error}</Alert>}

      <Box sx={{ 
          mb: 1.5, 
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
      }}>
          <Grid container spacing={1} alignItems="center">
            {/* Filtro Tipo */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel size="small" sx={{ fontSize: '0.875rem' }}>Tipo</InputLabel>
                <Select
                  value={typeFilter}
                  label="Tipo"
                  onChange={handleTypeFilterChange}
                  disabled={typeOptions.length === 0}
                  size="small"
                  sx={{ height: 40, fontSize: '0.875rem' }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.875rem' }}><em>Todos</em></MenuItem>
                  {typeOptions.map(opt => <MenuItem key={opt.id} value={String(opt.id)} sx={{ fontSize: '0.875rem' }}>{opt.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
             {/* Filtro Marca */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                 <InputLabel size="small" sx={{ fontSize: '0.875rem' }}>Marca</InputLabel>
                <Select
                  value={brandFilter}
                  label="Marca"
                  onChange={handleBrandFilterChange}
                  disabled={brandOptions.length === 0}
                  size="small"
                  sx={{ height: 40, fontSize: '0.875rem' }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.875rem' }}><em>Todas</em></MenuItem>
                  {brandOptions.map(opt => <MenuItem key={opt.id} value={String(opt.id)} sx={{ fontSize: '0.875rem' }}>{opt.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {/* Filtro Número de Série */}
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                label="Nº Série"
                value={serialFilter}
                onChange={handleSerialFilterChange}
                placeholder="Buscar por Nº Série..."
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{ sx: { height: 40, fontSize: '0.875rem' } }}
              />
            </Grid>
            {/* Botões Ação Filtro */}
            <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<Search size={16} />}
                    size="small"
                    sx={{ 
                      py: 0.5, 
                      px: 2,
                      height: 36,
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      borderRadius: 1
                    }}
                >
                    BUSCAR
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    startIcon={<RefreshCcw size={16} />}
                    size="small"
                    sx={{ 
                      py: 0.5, 
                      px: 2,
                      height: 36,
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      borderRadius: 1
                    }}
                >
                    LIMPAR
                </Button>
                </Stack>
            </Grid>
          </Grid>
      </Box>

      {/* Card da Tabela */}
      <Card elevation={2}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}> 
          <DataGrid
            autoHeight
            rows={equipments}
            columns={columns}
            loading={loading}
            rowCount={totalRowCount}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            localeText={{...ptBRGridLocale, ...ptBRLocaleText}}
            sx={getDataGridStyles(theme)}
            density="compact"
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Diálogo de Exclusão/Baixa */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Baixa/Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja marcar o equipamento 
            '{equipmentToDelete?.EquipmentType?.name} {equipmentToDelete?.EquipmentBrand?.name} (Série: {equipmentToDelete?.serial_number || 'N/A'})'
            como baixado? Esta ação pode ser reversível dependendo da lógica de negócio.
            (Implementar API de exclusão/baixa)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} size="small" sx={{ fontSize: '0.875rem' }}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" size="small" sx={{ fontSize: '0.875rem' }}>Confirmar Baixa</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentListPage; 